import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle, Clock, User, Store, Calendar, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';

interface PendingSeller {
  id: string;
  full_name: string;
  username: string;
  business_name: string;
  business_description: string;
  business_category: string;
  phone: string;
  whatsapp_number: string;
  verified: boolean;
  created_at: string;
  email?: string;
}

export default function SellerApproval() {
  const [pendingSellers, setPendingSellers] = useState<PendingSeller[]>([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchPendingSellers();
  }, []);

  const fetchPendingSellers = async () => {
    try {
      // Use Edge Function to securely fetch pending sellers with emails
      const { data, error } = await supabase.functions.invoke('get-pending-sellers');

      if (error) throw error;

      if (data?.sellers) {
        setPendingSellers(data.sellers);
      }
    } catch (error) {
      console.error('Error fetching pending sellers:', error);
      toast({
        title: 'Error',
        description: 'Failed to load pending sellers',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const approveSeller = async (sellerId: string) => {
    setApproving(sellerId);
    
    try {
      // Get current user (admin)
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

          // Call the notification handler to approve seller
          const { data, error } = await supabase.functions.invoke('notification-handler', {
            body: {
              action: 'approve_seller',
              data: {
                sellerId,
                adminId: user.id
              }
            }
          });

      if (error) throw error;

      toast({
        title: 'Seller Approved!',
        description: 'The seller has been approved and will receive notifications.',
      });

      // Refresh the list
      fetchPendingSellers();
    } catch (error: any) {
      console.error('Error approving seller:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to approve seller',
        variant: 'destructive',
      });
    } finally {
      setApproving(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-8 bg-muted animate-pulse rounded" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="space-y-3">
                  <div className="h-6 bg-muted animate-pulse rounded w-1/3" />
                  <div className="h-4 bg-muted animate-pulse rounded w-2/3" />
                  <div className="h-4 bg-muted animate-pulse rounded w-1/2" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Seller Approvals</h2>
          <p className="text-muted-foreground">
            Review and approve pending seller applications
          </p>
        </div>
        <Badge variant="secondary" className="text-lg px-3 py-1">
          {pendingSellers.length} Pending
        </Badge>
      </div>

      {pendingSellers.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
            <h3 className="text-lg font-semibold mb-2">All Caught Up!</h3>
            <p className="text-muted-foreground text-center">
              There are no pending seller applications at the moment.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6">
          {pendingSellers.map((seller) => (
            <Card key={seller.id} className="border-l-4 border-l-orange-500">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <CardTitle className="flex items-center gap-2">
                      <Store className="h-5 w-5" />
                      {seller.business_name || 'Business Name Not Provided'}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {seller.full_name || seller.username}
                      {seller.email && ` • ${seller.email}`}
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="text-orange-600">
                    <Clock className="h-3 w-3 mr-1" />
                    Pending
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground mb-2">
                      Business Information
                    </h4>
                    <div className="space-y-1">
                      <p><strong>Category:</strong> {seller.business_category || 'Not specified'}</p>
                      <p><strong>Description:</strong></p>
                      <p className="text-sm text-muted-foreground">
                        {seller.business_description || 'No description provided'}
                      </p>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-sm text-muted-foreground mb-2">
                      Contact Information
                    </h4>
                    <div className="space-y-1">
                      {seller.phone && (
                        <p><strong>Phone:</strong> {seller.phone}</p>
                      )}
                      {seller.whatsapp_number && (
                        <p><strong>WhatsApp:</strong> {seller.whatsapp_number}</p>
                      )}
                      <p className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        Applied: {format(new Date(seller.created_at), 'MMM dd, yyyy')}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    onClick={() => approveSeller(seller.id)}
                    disabled={approving === seller.id}
                    className="flex-1"
                  >
                    {approving === seller.id ? (
                      <>
                        <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                        Approving...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Approve Seller
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      // Could implement reject functionality later
                      toast({
                        title: 'Feature Coming Soon',
                        description: 'Reject functionality will be added in a future update.',
                      });
                    }}
                  >
                    <AlertCircle className="h-4 w-4 mr-2" />
                    Review Later
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}